import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { getRoomImages, listRooms } from '../api/rooms';
import { getMaterials, getProducts } from '../api/supplierInteriors';

function normalizeListResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.rooms)) return data.rooms;
  return [];
}

function getSearchQueryFromUrl() {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('q')?.trim() ?? '';
}

async function getAllProducts(pageSize = 100) {
  const safePageSize = Math.min(100, Math.max(1, pageSize));
  let page = 1;
  let all = [];
  let total = null;

  while (true) {
    const res = await getProducts(page, safePageSize);
    const items = Array.isArray(res?.products) ? res.products : [];
    all = all.concat(items);
    total = typeof res?.total === 'number' ? res.total : total;

    if (items.length < safePageSize) break;
    if (total != null && all.length >= total) break;
    page += 1;
  }

  return all;
}

const Search = () => {
  const { user } = useAuth();
  const { setCurrentView, setRoomId, setPhotorealisticImage } = useSidebar();
  const [query, setQuery] = useState(getSearchQueryFromUrl);
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingSupplierData, setLoadingSupplierData] = useState(false);
  const [error, setError] = useState(null);

  const isSupplier = user?.userRoles?.some((role) => String(role).toLowerCase() === 'supplier');

  useEffect(() => {
    const syncQuery = () => setQuery(getSearchQueryFromUrl());
    window.addEventListener('popstate', syncQuery);
    window.addEventListener('dx-search-query-change', syncQuery);
    return () => {
      window.removeEventListener('popstate', syncQuery);
      window.removeEventListener('dx-search-query-change', syncQuery);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingProjects(true);
    setError(null);

    listRooms()
      .then((res) => {
        if (cancelled) return;
        const rooms = normalizeListResponse(res);
        setProjects(rooms);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Search: failed to load projects', err);
        setError(err?.message ?? 'Failed to load projects');
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingProjects(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const ids = projects.map((p) => p?.id).filter((id) => id != null);
    if (ids.length === 0) return;

    ids.forEach((id) => {
      getRoomImages(id)
        .then((images) => {
          if (!Array.isArray(images) || images.length === 0) return;

          const chosen = [...images].sort((a, b) => {
            const at = a?.createdAt ?? '';
            const bt = b?.createdAt ?? '';
            return new Date(bt) - new Date(at);
          })[0];

          const url = chosen?.s3Key ?? null;
          if (!url) return;

          setProjects((prev) =>
            prev.map((p) => (p.id === id ? { ...p, _thumbnailUrl: url } : p))
          );
        })
        .catch(() => {
          // Ignore thumbnail failures and keep text-only card.
        });
    });
  }, [projects.length]);

  useEffect(() => {
    if (!isSupplier) {
      setProducts([]);
      setMaterials([]);
      setLoadingSupplierData(false);
      return;
    }

    let cancelled = false;
    setLoadingSupplierData(true);

    Promise.all([getAllProducts(100), getMaterials()])
      .then(([productsRes, materialsRes]) => {
        if (cancelled) return;

        const allProducts = Array.isArray(productsRes) ? productsRes : [];
        const myProducts = allProducts.filter((p) => {
          if (typeof p?.supplierId !== 'number') return true;
          return p.supplierId === user?.id;
        });

        setProducts(myProducts);
        setMaterials(Array.isArray(materialsRes?.materials) ? materialsRes.materials : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Search: failed to load supplier data', err);
        setError((prev) => prev ?? err?.message ?? 'Failed to load supplier data');
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingSupplierData(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isSupplier, user?.id]);

  const normalizedQuery = query.toLowerCase();

  const filteredProjects = useMemo(
    () =>
      projects.filter((p) => {
        if (!normalizedQuery) return true;
        const name = p?.name ?? p?.title ?? '';
        const description = p?.description ?? '';
        return (
          String(name).toLowerCase().includes(normalizedQuery) ||
          String(description).toLowerCase().includes(normalizedQuery)
        );
      }),
    [projects, normalizedQuery]
  );

  const filteredProducts = useMemo(
    () =>
      products.filter((p) => {
        if (!normalizedQuery) return true;
        const name = p?.name ?? '';
        const type = p?.interiorType?.interiorType ?? '';
        const desc = p?.description?.text ?? p?.description ?? '';
        return (
          String(name).toLowerCase().includes(normalizedQuery) ||
          String(type).toLowerCase().includes(normalizedQuery) ||
          String(desc).toLowerCase().includes(normalizedQuery)
        );
      }),
    [products, normalizedQuery]
  );

  const filteredMaterials = useMemo(
    () =>
      materials.filter((m) => {
        if (!normalizedQuery) return true;
        const name = m?.materialType ?? m?.name ?? '';
        const idText = m?.id != null ? String(m.id) : '';
        return (
          String(name).toLowerCase().includes(normalizedQuery) ||
          idText.includes(normalizedQuery)
        );
      }),
    [materials, normalizedQuery]
  );

  const isLoading = loadingProjects || (isSupplier && loadingSupplierData);
  const hasAnyResult =
    filteredProjects.length > 0 ||
    (isSupplier && (filteredProducts.length > 0 || filteredMaterials.length > 0));

  return (
    <div className="mb-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Search Results</h2>
      <p className="text-sm text-gray-500 mb-6">
        {query ? `Showing results for "${query}"` : 'Showing all results'}
      </p>

      {error && <div className="mb-4 text-sm text-red-600">{error}</div>}
      {isLoading && <div className="text-sm text-gray-500 mb-6">Loading results...</div>}

      {filteredProjects.length > 0 && (
        <section className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Projects ({filteredProjects.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProjects.map((project) => (
              <button
                type="button"
                key={project.id}
                className="text-left border border-gray-200 rounded-lg overflow-hidden bg-white hover:shadow-sm transition-shadow"
                onClick={() => {
                  if (project.id == null) return;
                  setRoomId(project.id);
                  setPhotorealisticImage(null);
                  setCurrentView('Photorealistic');
                }}
              >
                <div className="h-[230px] bg-gray-100 overflow-hidden">
                  {project._thumbnailUrl ? (
                    <img
                      src={project._thumbnailUrl}
                      alt={project.name ?? project.title ?? `Project #${project.id}`}
                      className="w-full h-full object-cover"
                      crossOrigin="anonymous"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        setProjects((prev) =>
                          prev.map((p) =>
                            p.id === project.id ? { ...p, _thumbnailUrl: null } : p
                          )
                        );
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                      No preview
                    </div>
                  )}
                </div>
                <div className="p-4">
                <p className="font-medium text-gray-800 truncate">
                  {project.name ?? project.title ?? `Project #${project.id}`}
                </p>
                {project.description && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{project.description}</p>
                )}
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {isSupplier && (
        <>
          {filteredProducts.length > 0 && (
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Products ({filteredProducts.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="border border-gray-200 rounded-lg overflow-hidden bg-white"
                  >
                    <div className="h-32 bg-gray-100 overflow-hidden">
                      {product.images ? (
                        <img
                          src={product.images}
                          alt={product.name ?? `Product #${product.id}`}
                          className="w-full h-full object-cover"
                          crossOrigin="anonymous"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            setProducts((prev) =>
                              prev.map((item) =>
                                item.id === product.id ? { ...item, images: null } : item
                              )
                            );
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                          No preview
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="font-medium text-gray-800 truncate">
                        {product.name ?? `Product #${product.id}`}
                      </p>
                      {product.interiorType?.interiorType && (
                        <p className="text-xs text-gray-500 mt-1">
                          Type: {product.interiorType.interiorType}
                        </p>
                      )}
                      {product.description?.text && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {product.description.text}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {filteredMaterials.length > 0 && (
            <section>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Materials ({filteredMaterials.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMaterials.map((material) => (
                  <div
                    key={material.id}
                    className="border border-gray-200 rounded-lg p-4 bg-white flex items-center gap-3"
                  >
                    {material.imageKey ? (
                      <img
                        src={material.imageKey}
                        alt={material.materialType ?? 'Material'}
                        className="w-10 h-10 rounded object-cover"
                        crossOrigin="anonymous"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded bg-gray-100" />
                    )}
                    <div>
                      <p className="font-medium text-gray-800">
                        {material.materialType ?? material.name ?? `Material #${material.id}`}
                      </p>
                      <p className="text-xs text-gray-500">ID: {material.id}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {!isLoading && !hasAnyResult && (
        <p className="text-sm text-gray-500">No results found.</p>
      )}
    </div>
  );
};

export default Search;
